/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { compose } from 'recompose';

import type { TrackingData } from 'js/lib/retracked';

import { Link, Typography2, useTheme } from '@coursera/cds-core';
import type { LinkProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import withSkillDetails from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/withSkillDetails';
import type { PropsFromGetSkillsQuery } from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/withSkillDetails';

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps>(Link);

const StarSvg = () => (
  <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.5 2L11.8154 6.60905L17 7.34664L13.25 10.9357L14.1345 16L9.5 13.6108L4.86553 16L5.75 10.9357L2 7.34664L7.18097 6.60905L9.5 2Z"
      fill="#FFC500"
      stroke="#EBA600"
      strokeWidth="1.61616"
      strokeLinecap="round"
    />
  </svg>
);

const useStyles = () => {
  const { palette, spacing, breakpoints } = useTheme();

  return {
    skillCard: css({
      display: 'flex',
      alignItems: 'center',
      gap: spacing(12),
      padding: spacing(16),
      border: `1px solid ${palette.gray[300]}`,
      borderRadius: 4,
      background: palette.white,
      textDecoration: 'none',
      flex: '1 1 20%',

      transition: 'all 0.1s',

      ':hover': {
        textDecoration: 'underline',
        background: palette.blue[100],
      },

      [breakpoints.down('md')]: {
        flex: '1 1 40%',
      },
      [breakpoints.down('xs')]: {
        flex: '1 1 100%',
      },
    }),
  };
};

type PropsFromCaller = {
  skillId: string;
  baseUrl: string;
  trackingData: TrackingData;
};

type Props = PropsFromCaller & PropsFromGetSkillsQuery;

export const RelatedSkillEntry = ({ skillId, trackingData, baseUrl, skill }: Props): React.ReactElement => {
  const styles = useStyles();

  return (
    <TrackedLink
      {...Link.defaultProps}
      trackingName="related_skill_link"
      trackingData={{
        ...trackingData,
        skillId,
      }}
      css={styles.skillCard}
      tabIndex={0}
      href={`${baseUrl}/skills/${skillId}?source=related-skill`}
    >
      <StarSvg />
      <Typography2 color="highlightBlue" variant="subtitleMedium" component="span">
        {/* skillId is almost human-readable (ie: "machine-learning"), so it makes a good fallback */}
        {skill?.skillName ?? skillId}
      </Typography2>
    </TrackedLink>
  );
};

export default compose<Props, PropsFromCaller>(withSkillDetails)(RelatedSkillEntry);
